import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import Button from '../components/Button';
import { fetchSkiAreas, fetchSkiAreaDag } from '../services/skiAreasService';
import { useError } from '../contexts/ErrorContext';
import { MAPBOX_STYLE } from '../constants/mapbox';
import './SkiAreaDagPage.css';

const MAPBOX_TOKEN = import.meta.env.VITE_MAPBOX_TOKEN;

const MAP_STYLES = {
  dark: { id: 'mapbox://styles/mapbox/dark-v11', label: 'Dark' },
  rekkie: { id: MAPBOX_STYLE, label: 'REKKIE' },
};

// Edge colors by type
const EDGE_COLORS = {
  run: {
    green: '#22c55e',
    blue: '#3b82f6',
    black: '#ffffff',
    double_black: '#ef4444',
  },
  lift: '#f59e0b',
  transfer: '#e879f9',
};

// Node colors
const NODE_COLORS = {
  hub: '#a855f7',
  lift: '#f59e0b',
  run: '#3b82f6',
  default: '#888888',
};

function getEdgeColor(edge) {
  if (edge.type === 'lift') return EDGE_COLORS.lift;
  if (edge.type === 'transfer') return EDGE_COLORS.transfer;
  if (edge.type === 'run') {
    return EDGE_COLORS.run[edge.metadata?.difficulty] || EDGE_COLORS.run.blue;
  }
  return '#888888';
}

function getNodeColor(node) {
  const hasLift = node.attachedFeatures.some(f => f.kind === 'lift');
  const hasRun = node.attachedFeatures.some(f => f.kind === 'run');
  if (hasLift && hasRun) return NODE_COLORS.hub;
  if (hasLift) return NODE_COLORS.lift;
  if (hasRun) return NODE_COLORS.run;
  return NODE_COLORS.default;
}

/**
 * Build GeoJSON FeatureCollections from DAG data for Mapbox layers.
 */
function buildMapGeoJson(dag) {
  const nodes = Object.values(dag.nodes);

  // Build edges as LineString features, grouped by color for styling
  const edgeFeatures = dag.edges.map(edge => {
    const fromNode = dag.nodes[edge.from];
    const toNode = dag.nodes[edge.to];
    if (!fromNode || !toNode) return null;

    return {
      type: 'Feature',
      properties: {
        color: getEdgeColor(edge),
        type: edge.type,
        name: edge.metadata?.name || '',
        difficulty: edge.metadata?.difficulty || '',
      },
      geometry: {
        type: 'LineString',
        coordinates: [fromNode.coordinates, toNode.coordinates],
      },
    };
  }).filter(Boolean);

  // Build nodes as Point features
  const nodeFeatures = nodes.map(node => {
    const features = node.attachedFeatures.map(f => `${f.featureName} (${f.kind} ${f.role})`).join(', ');
    return {
      type: 'Feature',
      properties: {
        id: node.id,
        color: getNodeColor(node),
        elevation: node.elevation,
        features,
      },
      geometry: {
        type: 'Point',
        coordinates: node.coordinates,
      },
    };
  });

  return {
    edges: { type: 'FeatureCollection', features: edgeFeatures },
    nodes: { type: 'FeatureCollection', features: nodeFeatures },
  };
}

/**
 * Animated network background for the selection screen.
 * Renders a drifting constellation of nodes + edges that evokes a trail graph.
 */
const NetworkBackground = () => {
  const canvasRef = useRef(null);
  const animRef = useRef(null);
  const nodesRef = useRef(null);

  const COLORS = useMemo(() => [
    '#667eea', '#a855f7', '#3b82f6', '#22c55e', '#f59e0b',
  ], []);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');

    const resize = () => {
      const dpr = window.devicePixelRatio || 1;
      const rect = canvas.parentElement.getBoundingClientRect();
      canvas.width = rect.width * dpr;
      canvas.height = rect.height * dpr;
      canvas.style.width = `${rect.width}px`;
      canvas.style.height = `${rect.height}px`;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    resize();
    window.addEventListener('resize', resize);

    // Init particles
    const COUNT = 60;
    const CONNECT_DIST = 160;
    if (!nodesRef.current) {
      const rect = canvas.parentElement.getBoundingClientRect();
      nodesRef.current = Array.from({ length: COUNT }, () => ({
        x: Math.random() * rect.width,
        y: Math.random() * rect.height,
        vx: (Math.random() - 0.5) * 0.4,
        vy: (Math.random() - 0.5) * 0.25,
        r: 1.5 + Math.random() * 2.5,
        color: COLORS[Math.floor(Math.random() * COLORS.length)],
        pulse: Math.random() * Math.PI * 2,
      }));
    }

    const draw = () => {
      const rect = canvas.parentElement.getBoundingClientRect();
      const w = rect.width;
      const h = rect.height;

      ctx.clearRect(0, 0, w, h);

      const particles = nodesRef.current;

      // Update positions
      for (const p of particles) {
        p.x += p.vx;
        p.y += p.vy;
        p.pulse += 0.015;
        if (p.x < -20) p.x = w + 20;
        if (p.x > w + 20) p.x = -20;
        if (p.y < -20) p.y = h + 20;
        if (p.y > h + 20) p.y = -20;
      }

      // Draw connections
      for (let i = 0; i < particles.length; i++) {
        for (let j = i + 1; j < particles.length; j++) {
          const a = particles[i];
          const b = particles[j];
          const dx = a.x - b.x;
          const dy = a.y - b.y;
          const dist = Math.sqrt(dx * dx + dy * dy);
          if (dist < CONNECT_DIST) {
            const alpha = (1 - dist / CONNECT_DIST) * 0.15;
            ctx.beginPath();
            ctx.strokeStyle = `rgba(102, 126, 234, ${alpha})`;
            ctx.lineWidth = 1;
            ctx.moveTo(a.x, a.y);
            ctx.lineTo(b.x, b.y);
            ctx.stroke();
          }
        }
      }

      // Draw particles
      for (const p of particles) {
        const glow = 0.4 + Math.sin(p.pulse) * 0.3;
        const r = p.r * (1 + Math.sin(p.pulse) * 0.2);

        // Outer glow
        ctx.beginPath();
        const grad = ctx.createRadialGradient(p.x, p.y, 0, p.x, p.y, r * 4);
        grad.addColorStop(0, p.color + Math.round(glow * 40).toString(16).padStart(2, '0'));
        grad.addColorStop(1, 'transparent');
        ctx.fillStyle = grad;
        ctx.arc(p.x, p.y, r * 4, 0, Math.PI * 2);
        ctx.fill();

        // Core
        ctx.beginPath();
        ctx.fillStyle = p.color;
        ctx.globalAlpha = 0.5 + glow * 0.5;
        ctx.arc(p.x, p.y, r, 0, Math.PI * 2);
        ctx.fill();
        ctx.globalAlpha = 1;
      }

      animRef.current = requestAnimationFrame(draw);
    };

    animRef.current = requestAnimationFrame(draw);

    return () => {
      window.removeEventListener('resize', resize);
      if (animRef.current) cancelAnimationFrame(animRef.current);
    };
  }, [COLORS]);

  return <canvas ref={canvasRef} className="ski-dag-bg-canvas" />;
};

/**
 * Build a searchable index of features (runs/lifts) from DAG nodes.
 * Returns an array of { featureName, kind, role, nodeId, coordinates, elevation }.
 */
function buildFeatureIndex(dag) {
  if (!dag) return [];
  const entries = [];
  for (const node of Object.values(dag.nodes)) {
    for (const f of node.attachedFeatures) {
      if (!f.featureName) continue;
      entries.push({
        featureName: f.featureName,
        kind: f.kind,
        role: f.role,
        nodeId: node.id,
        coordinates: node.coordinates,
        elevation: node.elevation,
      });
    }
  }
  // Deduplicate by featureName + role (same feature may appear on merged nodes)
  const seen = new Set();
  return entries.filter(e => {
    const key = `${e.featureName}::${e.role}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

const SkiAreaDagPage = () => {
  const navigate = useNavigate();
  const { addError } = useError();
  const mapContainerRef = useRef(null);
  const mapRef = useRef(null);
  const popupRef = useRef(null);
  const highlightMarkerRef = useRef(null);

  // State
  const [skiAreas, setSkiAreas] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedArea, setSelectedArea] = useState(null);
  const [dag, setDag] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingAreas, setLoadingAreas] = useState(true);
  const [error, setError] = useState(null);

  // Feature search state
  const [featureQuery, setFeatureQuery] = useState('');
  const [featureIndex, setFeatureIndex] = useState([]);

  // Map style state
  const [mapStyle, setMapStyle] = useState('dark');
  const [mapOpacity, setMapOpacity] = useState(1);

  // Build feature index when dag changes
  useEffect(() => {
    setFeatureIndex(buildFeatureIndex(dag));
    setFeatureQuery('');
  }, [dag]);

  // Filter features by search
  const featureQueryLower = featureQuery.toLowerCase().trim();
  const filteredFeatures = featureQueryLower
    ? featureIndex.filter(f => f.featureName.toLowerCase().includes(featureQueryLower)).slice(0, 30)
    : [];

  // Group filtered features by featureName
  const groupedFeatures = [];
  const seenNames = new Set();
  for (const f of filteredFeatures) {
    if (!seenNames.has(f.featureName)) {
      seenNames.add(f.featureName);
      groupedFeatures.push({
        featureName: f.featureName,
        kind: f.kind,
        entries: filteredFeatures.filter(e => e.featureName === f.featureName),
      });
    }
  }

  // Handle clicking a feature result — fly to that node
  const handleFeatureClick = useCallback((entry) => {
    const map = mapRef.current;
    if (!map) return;

    // Remove previous highlight marker
    if (highlightMarkerRef.current) {
      highlightMarkerRef.current.remove();
      highlightMarkerRef.current = null;
    }

    // Fly to the node
    map.flyTo({
      center: entry.coordinates,
      zoom: 16,
      duration: 1200,
    });

    // Add a pulsing highlight marker
    const el = document.createElement('div');
    el.className = 'ski-dag-highlight-marker';
    highlightMarkerRef.current = new mapboxgl.Marker({ element: el, anchor: 'center' })
      .setLngLat(entry.coordinates)
      .addTo(map);

    // Show popup
    if (popupRef.current) popupRef.current.remove();
    popupRef.current = new mapboxgl.Popup({ closeButton: true, offset: 18 })
      .setLngLat(entry.coordinates)
      .setHTML(`<div style="font-size:12px;">
        <div style="font-weight:600;">${entry.featureName}</div>
        <div style="color:#aaa;">${entry.kind} — ${entry.role}</div>
        <div style="color:#aaa;">Elev: ${entry.elevation.toFixed(0)}m</div>
      </div>`)
      .addTo(map);

    setFeatureQuery('');
  }, []);

  // Load ski areas on mount
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const areas = await fetchSkiAreas();
        if (!cancelled) {
          setSkiAreas(areas.sort((a, b) => a.name.localeCompare(b.name)));
          setLoadingAreas(false);
        }
      } catch (err) {
        if (!cancelled) {
          addError(err.message, err.stack, 'DAG Page');
          setLoadingAreas(false);
        }
      }
    })();
    return () => { cancelled = true; };
  }, [addError]);

  // Filter ski areas by search
  const query = searchQuery.toLowerCase().trim();
  const filteredAreas = query
    ? skiAreas.filter(a =>
        a.name.toLowerCase().includes(query) ||
        (a.region || '').toLowerCase().includes(query) ||
        (a.country || '').toLowerCase().includes(query)
      ).slice(0, 20)
    : [];

  // Handle ski area selection
  const handleSelectArea = useCallback(async (area) => {
    setSelectedArea(area);
    setSearchQuery('');
    setDag(null);
    setError(null);
    setLoading(true);

    try {
      const dagData = await fetchSkiAreaDag(area.id);
      setDag(dagData);
    } catch (err) {
      setError(err.message);
      addError(err.message, err.stack, 'DAG Page');
    } finally {
      setLoading(false);
    }
  }, [addError]);

  // Add DAG layers + hover handlers to the map
  const addDagLayers = useCallback((map, dagData, fitBounds) => {
    if (!dagData) return;
    const { edges, nodes } = buildMapGeoJson(dagData);

    // Remove existing sources/layers if any
    ['dag-transfers', 'dag-edges', 'dag-nodes'].forEach(id => {
      if (map.getLayer(id)) map.removeLayer(id);
    });
    ['dag-edges-src', 'dag-nodes-src'].forEach(id => {
      if (map.getSource(id)) map.removeSource(id);
    });

    // Edges source
    map.addSource('dag-edges-src', { type: 'geojson', data: edges });

    // Transfer edges (dashed)
    map.addLayer({
      id: 'dag-transfers',
      type: 'line',
      source: 'dag-edges-src',
      filter: ['==', ['get', 'type'], 'transfer'],
      layout: { 'line-join': 'round', 'line-cap': 'round' },
      paint: {
        'line-color': ['get', 'color'],
        'line-width': 1.5,
        'line-opacity': 0.4,
        'line-dasharray': [3, 3],
      },
    });

    // Run + lift edges (solid)
    map.addLayer({
      id: 'dag-edges',
      type: 'line',
      source: 'dag-edges-src',
      filter: ['!=', ['get', 'type'], 'transfer'],
      layout: { 'line-join': 'round', 'line-cap': 'round' },
      paint: {
        'line-color': ['get', 'color'],
        'line-width': 3,
        'line-opacity': 0.85,
      },
    });

    // Nodes source
    map.addSource('dag-nodes-src', { type: 'geojson', data: nodes });

    map.addLayer({
      id: 'dag-nodes',
      type: 'circle',
      source: 'dag-nodes-src',
      paint: {
        'circle-radius': 5,
        'circle-color': ['get', 'color'],
        'circle-stroke-width': 1,
        'circle-stroke-color': 'rgba(255,255,255,0.5)',
      },
    });

    // Fit to bounds on initial load
    if (fitBounds) {
      const allCoords = Object.values(dagData.nodes).map(n => n.coordinates);
      if (allCoords.length > 0) {
        const bounds = allCoords.reduce(
          (b, c) => b.extend(c),
          new mapboxgl.LngLatBounds(allCoords[0], allCoords[0])
        );
        map.fitBounds(bounds, { padding: 60, maxZoom: 15 });
      }
    }

    // Hover popup on nodes
    map.on('mouseenter', 'dag-nodes', (e) => {
      map.getCanvas().style.cursor = 'pointer';
      const props = e.features[0].properties;
      const coords = e.features[0].geometry.coordinates.slice();
      const html = `<div style="font-size:12px;max-width:220px;">
        <div style="font-weight:600;margin-bottom:4px;">${props.id}</div>
        <div style="color:#aaa;">Elev: ${Number(props.elevation).toFixed(0)}m</div>
        <div style="color:#aaa;margin-top:2px;">${props.features}</div>
      </div>`;

      if (popupRef.current) popupRef.current.remove();
      popupRef.current = new mapboxgl.Popup({ closeButton: false, closeOnClick: false, offset: 12 })
        .setLngLat(coords)
        .setHTML(html)
        .addTo(map);
    });

    map.on('mouseleave', 'dag-nodes', () => {
      map.getCanvas().style.cursor = '';
      if (popupRef.current) {
        popupRef.current.remove();
        popupRef.current = null;
      }
    });
  }, []);

  // Initialize map once the graph container mounts
  useEffect(() => {
    if (!mapContainerRef.current || mapRef.current) return;

    mapboxgl.accessToken = MAPBOX_TOKEN;

    const map = new mapboxgl.Map({
      container: mapContainerRef.current,
      style: MAP_STYLES[mapStyle].id,
      center: [0, 0],
      zoom: 2,
    });

    map.addControl(new mapboxgl.NavigationControl(), 'top-right');
    mapRef.current = map;

    // Add DAG layers once map loads
    const onLoad = () => addDagLayers(map, dag, true);
    map.once('load', onLoad);

    return () => {
      if (popupRef.current) popupRef.current.remove();
      map.remove();
      mapRef.current = null;
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedArea, dag]); // re-init when graph container appears

  // Set opacity on all base style layers (not DAG layers)
  const applyBaseLayerOpacity = useCallback((map, opacity) => {
    if (!map.getStyle()) return;
    const dagLayerIds = ['dag-transfers', 'dag-edges', 'dag-nodes'];
    for (const layer of map.getStyle().layers) {
      if (dagLayerIds.includes(layer.id)) continue;
      try {
        if (layer.type === 'background') {
          map.setPaintProperty(layer.id, 'background-opacity', opacity);
        } else if (layer.type === 'fill') {
          map.setPaintProperty(layer.id, 'fill-opacity', opacity);
        } else if (layer.type === 'line') {
          map.setPaintProperty(layer.id, 'line-opacity', opacity);
        } else if (layer.type === 'symbol') {
          map.setPaintProperty(layer.id, 'icon-opacity', opacity);
          map.setPaintProperty(layer.id, 'text-opacity', opacity);
        } else if (layer.type === 'raster') {
          map.setPaintProperty(layer.id, 'raster-opacity', opacity);
        } else if (layer.type === 'circle') {
          map.setPaintProperty(layer.id, 'circle-opacity', opacity);
        } else if (layer.type === 'fill-extrusion') {
          map.setPaintProperty(layer.id, 'fill-extrusion-opacity', opacity);
        } else if (layer.type === 'heatmap') {
          map.setPaintProperty(layer.id, 'heatmap-opacity', opacity);
        } else if (layer.type === 'hillshade') {
          map.setPaintProperty(layer.id, 'hillshade-exaggeration', opacity);
        }
      } catch {
        // Some layers may not support the property — skip
      }
    }
  }, []);

  // Handle style switching — setStyle and re-add layers after style loads
  const handleStyleToggle = useCallback(() => {
    const newStyle = mapStyle === 'dark' ? 'rekkie' : 'dark';
    setMapStyle(newStyle);

    const map = mapRef.current;
    if (!map) return;

    map.setStyle(MAP_STYLES[newStyle].id);

    // Re-add DAG layers after new style finishes loading (don't re-fit bounds)
    map.once('style.load', () => {
      addDagLayers(map, dag, false);
      applyBaseLayerOpacity(map, mapOpacity);
    });
  }, [mapStyle, dag, mapOpacity, addDagLayers, applyBaseLayerOpacity]);

  // Handle opacity slider change
  const handleOpacityChange = useCallback((e) => {
    const opacity = parseFloat(e.target.value);
    setMapOpacity(opacity);
    const map = mapRef.current;
    if (map && map.isStyleLoaded()) {
      applyBaseLayerOpacity(map, opacity);
    }
  }, [applyBaseLayerOpacity]);

  return (
    <div className="ski-dag-page">
      <header className="ski-dag-header">
        <div className="ski-dag-header-content">
          <div className="header-left">
            <Button
              variant="outline"
              size="medium"
              onClick={() => navigate('/tools')}
              className="back-button"
            >
              &larr;
            </Button>
            <h1>{selectedArea ? `${selectedArea.name} — Trail Graph` : 'Ski Area Trail Graph'}</h1>
          </div>
          {selectedArea && (
            <Button variant="outline" size="small" onClick={() => { setSelectedArea(null); setDag(null); setError(null); }}>
              Change Resort
            </Button>
          )}
        </div>
      </header>

      <main className="ski-dag-main">
        {!selectedArea && (
          <div className="ski-dag-select-container">
            <NetworkBackground />
            <h2>Select a Ski Area</h2>
            <p>Search for a resort to generate and visualize its trail graph</p>
            <div className="ski-dag-search-wrapper">
              <input
                type="text"
                className="ski-dag-search-input"
                placeholder={loadingAreas ? 'Loading ski areas...' : 'Search by name, region, or country...'}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                disabled={loadingAreas}
                autoFocus
              />
              {filteredAreas.length > 0 && (
                <div className="ski-dag-search-results">
                  {filteredAreas.map(area => (
                    <div
                      key={area.id}
                      className="ski-dag-search-item"
                      onClick={() => handleSelectArea(area)}
                    >
                      <div className="ski-dag-search-item-name">{area.name}</div>
                      <div className="ski-dag-search-item-meta">
                        {area.region}, {area.country}
                        {area.navigationCapable ? ' • Nav Available' : ''}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {selectedArea && loading && (
          <div className="ski-dag-loading">
            <div className="ski-dag-loading-spinner" />
            <p>Building trail graph for {selectedArea.name}...</p>
          </div>
        )}

        {selectedArea && error && (
          <div className="ski-dag-error">
            <p>Error: {error}</p>
            <Button variant="outline" size="small" onClick={() => handleSelectArea(selectedArea)}>
              Retry
            </Button>
          </div>
        )}

        {selectedArea && dag && !loading && (
          <div className="ski-dag-graph-container">
            <div ref={mapContainerRef} className="ski-dag-map" />

            <div className="ski-dag-stats">
              <div className="ski-dag-stats-title">{dag.skiAreaName}</div>
              <span>Nodes: {dag.stats.nodeCount}</span>
              <span>Run edges: {dag.stats.runEdges}</span>
              <span>Lift edges: {dag.stats.liftEdges}</span>
              <span>Transfer edges: {dag.stats.transferEdges}</span>
            </div>

            <div className="ski-dag-map-controls">
              <button
                className="ski-dag-style-toggle"
                onClick={handleStyleToggle}
                title={`Switch to ${mapStyle === 'dark' ? 'REKKIE' : 'Dark'} style`}
              >
                {mapStyle === 'dark' ? 'REKKIE' : 'Dark'}
              </button>
              <div className="ski-dag-opacity-control">
                <label>Map</label>
                <input
                  type="range"
                  min="0"
                  max="1"
                  step="0.05"
                  value={mapOpacity}
                  onChange={handleOpacityChange}
                  className="ski-dag-opacity-slider"
                />
              </div>
            </div>

            <div className="ski-dag-feature-search">
              <input
                type="text"
                className="ski-dag-feature-input"
                placeholder="Search runs & lifts..."
                value={featureQuery}
                onChange={(e) => setFeatureQuery(e.target.value)}
              />
              {groupedFeatures.length > 0 && (
                <div className="ski-dag-feature-results">
                  {groupedFeatures.map(group => (
                    <div key={group.featureName} className="ski-dag-feature-group">
                      <div className="ski-dag-feature-group-name">
                        <span className="ski-dag-feature-kind-badge" data-kind={group.kind}>
                          {group.kind}
                        </span>
                        {group.featureName}
                      </div>
                      <div className="ski-dag-feature-group-entries">
                        {group.entries.map(entry => (
                          <div
                            key={`${entry.nodeId}-${entry.role}`}
                            className="ski-dag-feature-entry"
                            onClick={() => handleFeatureClick(entry)}
                          >
                            <span className="ski-dag-feature-role">{entry.role}</span>
                            <span className="ski-dag-feature-elev">{entry.elevation.toFixed(0)}m</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="ski-dag-legend">
              <div className="ski-dag-legend-item">
                <div className="ski-dag-legend-line" style={{ background: EDGE_COLORS.run.green }} />
                <span>Green run</span>
              </div>
              <div className="ski-dag-legend-item">
                <div className="ski-dag-legend-line" style={{ background: EDGE_COLORS.run.blue }} />
                <span>Blue run</span>
              </div>
              <div className="ski-dag-legend-item">
                <div className="ski-dag-legend-line" style={{ background: EDGE_COLORS.run.black }} />
                <span>Black run</span>
              </div>
              <div className="ski-dag-legend-item">
                <div className="ski-dag-legend-line" style={{ background: EDGE_COLORS.run.double_black }} />
                <span>Double black</span>
              </div>
              <div className="ski-dag-legend-item">
                <div className="ski-dag-legend-line" style={{ background: EDGE_COLORS.lift }} />
                <span>Lift</span>
              </div>
              <div className="ski-dag-legend-item">
                <div className="ski-dag-legend-line" style={{ background: EDGE_COLORS.transfer, borderTop: '2px dashed #e879f9', height: 0 }} />
                <span>Transfer</span>
              </div>
              <div className="ski-dag-legend-item">
                <div className="ski-dag-legend-circle" style={{ background: '#a855f7' }} />
                <span>Hub (lift + run)</span>
              </div>
              <div className="ski-dag-legend-item">
                <div className="ski-dag-legend-circle" style={{ background: '#f59e0b' }} />
                <span>Lift station</span>
              </div>
              <div className="ski-dag-legend-item">
                <div className="ski-dag-legend-circle" style={{ background: '#3b82f6' }} />
                <span>Run endpoint</span>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default SkiAreaDagPage;
